import { useState, type SubmitEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import {
  AuthMethodsResponseSchema,
  PasswordLoginPendingSchema,
  RecoveryCodesResponseSchema,
  SecondFactorResponseSchema,
  type AuthMethodsResponse,
  type AuthenticatorEnrolment,
} from '@taqon/contracts';
import { ApiError, getJson } from '../../shared/api/client';
import { postAuth } from './auth-api';
import {
  AuthCard,
  AuthenticatorDetails,
  CodeField,
  PasswordField,
  RecoveryCodes,
} from './AuthCard';

/**
 * The unauthenticated state: sign-in only. Users are created by an administrator (SDD §11.3), so
 * there is no registration; a user without a password receives a setup link from an administrator.
 *
 * The methods come from `/auth/methods`:
 * - built-in sign-in (SDD §11.1.2): email and password, then the authenticator code or a recovery
 *   code — the production method;
 * - the organization sign-in leaves the SPA for `/auth/login` (OIDC), where configured;
 * - the temporary local login (SDD §11.1.1), where still enabled, below the others.
 * If the methods cannot be read (for example a 429 from the sign-in rate limit), no method is
 * offered — an API failure says nothing about which methods exist — and the page offers a retry.
 */
export function SignInPage() {
  const methods = useQuery({
    queryKey: ['auth-methods'],
    retry: false,
    queryFn: () => getJson('/auth/methods', AuthMethodsResponseSchema),
  });
  const data = methods.data;
  const builtIn = data?.password === true;
  const offerOidc = data?.oidc === true;
  const local = data?.local === true;

  return (
    <AuthCard heading="Sign in to your account">
      {methods.isPending || (methods.isError && methods.isFetching) ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} aria-label="Loading sign-in options" />
        </Box>
      ) : methods.isError || data === undefined ? (
        <Stack spacing={2}>
          <Alert severity="error" variant="outlined">
            Unable to load sign-in options. Please try again.
          </Alert>
          <Button
            variant="contained"
            size="large"
            fullWidth
            disableElevation
            onClick={() => {
              void methods.refetch();
            }}
            sx={{ textTransform: 'none', fontWeight: 600, py: 1.25 }}
          >
            Try again
          </Button>
        </Stack>
      ) : (
        <Stack spacing={3}>
          {builtIn && <BuiltInSignInForm methods={data} />}
          {builtIn && offerOidc && <Divider>or</Divider>}
          {offerOidc && (
            <Button
              variant={builtIn || local ? 'outlined' : 'contained'}
              size="large"
              fullWidth
              disableElevation
              href="/auth/login"
              sx={{ textTransform: 'none', fontWeight: 600, py: 1.25 }}
            >
              Sign in with organization account
            </Button>
          )}
          {local && (builtIn || offerOidc) && <Divider>temporary test sign-in</Divider>}
          {local && <LocalSignInForm methods={data} />}
        </Stack>
      )}
    </AuthCard>
  );
}

type Step =
  | { kind: 'password' }
  | { kind: 'second-factor'; useRecoveryCode: boolean }
  /** No authenticator yet while one is required: it is added here, before anything is signed in. */
  | { kind: 'enrol'; enrolment: AuthenticatorEnrolment }
  | { kind: 'enrolled'; recoveryCodes: readonly string[] };

/**
 * Built-in sign-in (SDD §11.1.2): the password, then the second factor. Where the second factor is
 * not required, the API answers the password step with `none` and the user is signed in. Where it
 * is required and the user has no authenticator yet, the API answers `enrol`: the user adds one,
 * confirms its first code, keeps the recovery codes shown once, and only then is signed in.
 */
function BuiltInSignInForm({ methods }: { methods: AuthMethodsResponse }) {
  const [step, setStep] = useState<Step>({ kind: 'password' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const csrf = methods.csrfToken ?? '';

  const submitPassword = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const answer = await postAuth(
        '/auth/password-login',
        { email, password },
        PasswordLoginPendingSchema,
        csrf,
      );
      setPassword('');
      if (answer?.secondFactor === 'none') {
        // Signed in: a full reload, so the new session is read fresh from /api/v1/me.
        window.location.assign('/');
        return;
      }
      if (answer?.secondFactor === 'enrol') {
        setStep({ kind: 'enrol', enrolment: answer.enrolment });
        return;
      }
      setStep({ kind: 'second-factor', useRecoveryCode: false });
    } catch (caught) {
      setPassword('');
      setError(signInError(caught));
    } finally {
      setSubmitting(false);
    }
  };

  const submitSecondFactor = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (step.kind !== 'second-factor') return;
    setSubmitting(true);
    setError(null);
    try {
      await postAuth(
        '/auth/password-login/verify',
        step.useRecoveryCode ? { recoveryCode } : { code },
        SecondFactorResponseSchema,
        csrf,
      );
      // A full reload, as after sign-out: the new session is read fresh from /api/v1/me.
      window.location.assign('/');
    } catch (caught) {
      setCode('');
      setRecoveryCode('');
      if (
        caught instanceof ApiError &&
        caught.status === 401 &&
        caught.problem?.detail?.startsWith('Sign in again')
      ) {
        setStep({ kind: 'password' });
      }
      setError(signInError(caught));
      setSubmitting(false);
    }
  };

  const submitEnrolment = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await postAuth(
        '/auth/password-login/enrol',
        { code },
        RecoveryCodesResponseSchema,
        csrf,
      );
      setCode('');
      // Signed in by now; the codes are shown once before the application opens.
      setStep({ kind: 'enrolled', recoveryCodes: result?.recoveryCodes ?? [] });
    } catch (caught) {
      setCode('');
      if (
        caught instanceof ApiError &&
        caught.status === 401 &&
        caught.problem?.detail?.startsWith('Sign in again')
      ) {
        setStep({ kind: 'password' });
      }
      setError(signInError(caught));
    } finally {
      setSubmitting(false);
    }
  };

  if (step.kind === 'enrolled') {
    return (
      <Stack spacing={2.5}>
        <Alert severity="success" variant="outlined">
          Your authenticator is added and you are signed in. From now on, sign in with your email,
          your password and a code from the app.
        </Alert>
        <RecoveryCodes codes={step.recoveryCodes} />
        <Button
          variant="contained"
          size="large"
          fullWidth
          disableElevation
          onClick={() => {
            window.location.assign('/');
          }}
          sx={{ textTransform: 'none', fontWeight: 600, py: 1.25 }}
        >
          I have saved them, continue
        </Button>
      </Stack>
    );
  }

  if (step.kind === 'enrol') {
    return (
      <Box
        component="form"
        onSubmit={(e) => void submitEnrolment(e)}
        noValidate
        aria-label="Add an authenticator"
      >
        <Stack spacing={2.5}>
          <Typography variant="body2">
            Signing in now needs an authenticator app as well as your password. Scan this code with
            one (for example Microsoft Authenticator or Google Authenticator), then enter the
            6-digit code it shows.
          </Typography>
          <AuthenticatorDetails enrolment={step.enrolment} />
          {error !== null && (
            <Alert severity="error" variant="outlined">
              {error}
            </Alert>
          )}
          <CodeField autoFocus value={code} onCode={setCode} />
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disableElevation
            disabled={submitting || code.length !== 6}
            sx={{ textTransform: 'none', fontWeight: 600, py: 1.25 }}
          >
            {submitting ? 'Checking…' : 'Add authenticator and sign in'}
          </Button>
          <Link
            component="button"
            type="button"
            variant="body2"
            sx={{ alignSelf: 'flex-end' }}
            onClick={() => {
              setError(null);
              setCode('');
              setStep({ kind: 'password' });
            }}
          >
            Start again
          </Link>
        </Stack>
      </Box>
    );
  }

  if (step.kind === 'password') {
    return (
      <Box
        component="form"
        onSubmit={(e) => void submitPassword(e)}
        noValidate
        aria-label="Sign in"
      >
        <Stack spacing={2.5}>
          {error !== null && (
            <Alert severity="error" variant="outlined">
              {error}
            </Alert>
          )}
          <TextField
            label="Email"
            type="email"
            autoComplete="username"
            autoFocus
            required
            fullWidth
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
          <PasswordField
            label="Password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disableElevation
            disabled={submitting || email === '' || password === ''}
            sx={{ textTransform: 'none', fontWeight: 600, py: 1.25 }}
          >
            {submitting ? 'Checking…' : 'Continue'}
          </Button>
          <Typography variant="body2" color="text.secondary">
            New user, or locked out? Ask your administrator for a setup link.
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={(e) => void submitSecondFactor(e)}
      noValidate
      aria-label="Second factor"
    >
      <Stack spacing={2.5}>
        {error !== null && (
          <Alert severity="error" variant="outlined">
            {error}
          </Alert>
        )}
        <Typography variant="body2">
          {step.useRecoveryCode
            ? 'Enter one of your recovery codes. Each code works once.'
            : 'Enter the 6-digit code from your authenticator app.'}
        </Typography>
        {step.useRecoveryCode ? (
          <TextField
            label="Recovery code"
            autoComplete="off"
            autoFocus
            required
            fullWidth
            value={recoveryCode}
            onChange={(e) => {
              setRecoveryCode(e.target.value);
            }}
          />
        ) : (
          <CodeField autoFocus value={code} onCode={setCode} />
        )}
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disableElevation
          disabled={
            submitting || (step.useRecoveryCode ? recoveryCode.trim() === '' : code.length !== 6)
          }
          sx={{ textTransform: 'none', fontWeight: 600, py: 1.25 }}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
        <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => {
              setError(null);
              setStep({ kind: 'second-factor', useRecoveryCode: !step.useRecoveryCode });
            }}
          >
            {step.useRecoveryCode ? 'Use the authenticator code' : 'Use a recovery code'}
          </Link>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => {
              setError(null);
              setStep({ kind: 'password' });
            }}
          >
            Start again
          </Link>
        </Stack>
      </Stack>
    </Box>
  );
}

function signInError(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.status === 401) {
      return caught.problem?.detail?.startsWith('Sign in again') === true
        ? 'That took too long. Enter your email and password again.'
        : 'The email, password or code is not correct.';
    }
    return caught.message;
  }
  return 'Sign-in is unavailable right now. Try again shortly.';
}

type LoginState = { kind: 'idle' } | { kind: 'submitting' } | { kind: 'failed'; message: string };

/** The temporary local login (SDD §11.1.1): an internal testing exception, retired after go-live. */
function LocalSignInForm({ methods }: { methods: AuthMethodsResponse }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [state, setState] = useState<LoginState>({ kind: 'idle' });
  const submitting = state.kind === 'submitting';

  const submit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setState({ kind: 'submitting' });
    const res = await fetch('/auth/local-login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'X-CSRF-Token': methods.csrfToken ?? '',
      },
      body: JSON.stringify({ email, password }),
    }).catch(() => null);

    if (res?.ok === true) {
      window.location.assign('/');
      return;
    }
    setPassword('');
    setState({ kind: 'failed', message: failureMessage(res?.status) });
  };

  return (
    <Box component="form" onSubmit={(e) => void submit(e)} noValidate aria-label="Email sign-in">
      <Stack spacing={2.5}>
        {state.kind === 'failed' && (
          <Alert severity="error" variant="outlined">
            {state.message}
          </Alert>
        )}
        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          autoFocus={methods.password !== true}
          required
          fullWidth
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
        />
        <PasswordField
          label="Password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
        />
        <Button
          type="submit"
          variant={methods.password === true ? 'outlined' : 'contained'}
          size="large"
          fullWidth
          disableElevation
          disabled={submitting || email === '' || password === ''}
          sx={{ textTransform: 'none', fontWeight: 600, py: 1.25 }}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </Stack>
    </Box>
  );
}

function failureMessage(status: number | undefined): string {
  if (status === 401 || status === 422) return 'The email or password is not correct.';
  if (status === 429) return 'Too many sign-in attempts. Wait a minute and try again.';
  if (status === 403) return 'The sign-in form has expired. Reload the page and try again.';
  return 'Sign-in is unavailable right now. Try again shortly.';
}
